import { HttpClient, HttpHeaders, HttpResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, delay, EMPTY, Observable, of, tap, throwError, timer } from 'rxjs';
import { catchError, filter, retryWhen, switchMap, take } from 'rxjs/operators';
import dayjs from 'dayjs';

import { jwtDecode } from 'jwt-decode';
import { TOKEN_DATA_LOCALSTORAGE_KEY } from '../const/const';
import { AppRouting } from '../enums';
import { RefreshTokenPayload, TokenData } from '../models';
import { environment } from '../../environments/environment';

@Injectable({
  providedIn: 'root',
})
export class HttpAuth {
  private isRefreshing = false;
  private refreshTokenSubject: BehaviorSubject<any> = new BehaviorSubject<any>(null);

  private readonly router = inject(Router);
  private readonly http = inject(HttpClient);

  private get tokenData(): TokenData {
    const localTokenData = localStorage.getItem(TOKEN_DATA_LOCALSTORAGE_KEY);
    return localTokenData ? JSON.parse(localTokenData) : null;
  }

  public get needRefresh(): boolean {
    const data = this.tokenData;
    if (data) {
      try {
        const decoded = jwtDecode(data.token) as any;
        return dayjs(decoded.exp * 1000).isBefore(dayjs().utc());
      } catch (Error) {
        return false;
      }
    }
    return false;
  }

  // public get<T>(url: string): Observable<T> {
  //   return this.refresh().pipe(
  //     switchMap((token) =>
  //       this.http.get<T>(url, {
  //         headers: this.getAuthHeader(token),
  //         observe: 'response',
  //       })
  //     ),
  //     switchMap((response: HttpResponse<T>) => this.handleRedirect(response))
  //   );
  // }

  public get<T>(url: string): Observable<T> {
    let retryCount = 0;
    return this.refresh().pipe(
      switchMap((token) =>
        this.http.get<T>(url, {
          headers: this.getAuthHeader(token),
          observe: 'response',
        })
      ),
      switchMap((response: HttpResponse<T>) => this.handleRedirect(response)),
      retryWhen((errors) =>
        errors.pipe(
          switchMap((err) => {
            const shouldRetry =
              err.status === 401 && localStorage.getItem('refreshInProgress') !== null;
            if (shouldRetry && retryCount < 10) {
              retryCount++;
              return timer(20);
            }
            return throwError(() => err);
          })
        )
      )
    );
  }

  public getFile(url: string): Observable<Blob> {
    return this.refresh().pipe(
      switchMap((token) =>
        this.http.get(url, {
          headers: this.getAuthHeader(token),
          responseType: 'blob',
        })
      )
    );
  }

  public post<T>(url: string, data: any = null, options?: any): Observable<T> {
    return this.refresh().pipe(
      switchMap(
        (token) =>
          this.http.post<T>(url, data, {
            ...options,
            headers: this.getAuthHeader(token),
            observe: 'response',
          }) as Observable<HttpResponse<T>>
      ),
      switchMap((response: HttpResponse<T>) => this.handleRedirect(response))
    );
  }

  public put<T>(url: string, data?: any): Observable<T> {
    return this.refresh().pipe(
      switchMap((token) =>
        this.http.put<T>(url, data, {
          headers: this.getAuthHeader(token),
          observe: 'response',
        })
      ),
      switchMap((response: HttpResponse<T>) => this.handleRedirect(response))
    );
  }

  public patch<T>(url: string, data?: any): Observable<T> {
    return this.refresh().pipe(
      switchMap((token) =>
        this.http.patch<T>(url, data, {
          headers: this.getAuthHeader(token),
          observe: 'response',
        })
      ),
      switchMap((response: HttpResponse<T>) => this.handleRedirect(response))
    );
  }

  public delete(url: string): Observable<any> {
    return this.refresh().pipe(
      switchMap((token) =>
        this.http.delete(url, {
          headers: this.getAuthHeader(token),
          observe: 'response',
        })
      ),
      switchMap((response: HttpResponse<any>) => this.handleRedirect(response))
    );
  }

  private getAuthHeader(token: string): HttpHeaders {
    return new HttpHeaders({
      Authorization: `Bearer ${token}`,
    });
  }

  public refreshToken() {
    const payload: RefreshTokenPayload = {
      accessToken: this.tokenData?.token,
      refreshToken: this.tokenData?.refreshToken,
    };

    return this.http.post<TokenData>(`${environment.apiUrl}/api/Authentication/refresh`, payload);
  }

  public refresh(): Observable<string> {
    if (this.needRefresh) {
      if (!this.isRefreshing) {
        this.isRefreshing = true;
        this.refreshTokenSubject.next(null);
        return this.refreshToken().pipe(
          take(1),
          switchMap((response) => {
            this.isRefreshing = false;
            localStorage.setItem(TOKEN_DATA_LOCALSTORAGE_KEY, JSON.stringify(response));
            this.refreshTokenSubject.next(true);
            return of(this.tokenData?.token);
          }),
          catchError((err) => {
            localStorage.removeItem(TOKEN_DATA_LOCALSTORAGE_KEY);
            return of('').pipe(
              delay(50),
              tap(() => {
                this.router.navigate([AppRouting.Login]);
                this.isRefreshing = false;
              })
            );
          })
        );
      } else {
        return this.refreshTokenSubject.pipe(
          filter((token) => token),
          take(1),
          switchMap(() => of(this.tokenData?.token))
        );
      }
    }
    return of(this.tokenData?.token);
  }

  private handleRedirect<T>(response: HttpResponse<T>): Observable<T> {
    if ([301, 302, 303].includes(response.status)) {
      const redirectLocation = response.headers.get('Location');
      if (redirectLocation) {
        window.location.href = redirectLocation;
      }
      return EMPTY;
    }
    return of(response.body as T);
  }
}
